import {
  AppliedPtoStatus,
  PromoCodeApiType,
  PromoCodeDistributionEmailStatus,
  PromoCodeDistributionSendMode,
  PromoCodeDistributionSummary,
  PromoCodeDistributionType,
  PromoCodeFormMode,
  PromoCodeItem,
  PromoCodeStatus,
  PromoCodeTotalSupplyType,
  PromoCodeType,
  PromoCodeUsageLimit,
} from 'Modules'

import { randomInt } from 'utils/random'

export const PROMO_CODE_STATUS: Record<PromoCodeStatus, PromoCodeStatus> = {
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
}

export const PROMO_CODE_STATUS_CONFIG: Record<
  PromoCodeStatus,
  { label: string; color: string; background: string }
> = {
  ACTIVE: { label: 'Active', color: '#008236', background: '#dcfce7' },
  SCHEDULED: { label: 'Scheduled', color: '#1447e6', background: '#dbeafe' },
  EXPIRED: { label: 'Expired', color: '#364153', background: '#f3f4f6' },
}

/** Status filter options (the "All Status" sentinel is added at the call site). */
export const PROMO_CODE_STATUS_OPTIONS: { value: PromoCodeStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'EXPIRED', label: 'Expired' },
]

export const PROMO_CODE_TYPE_CONFIG: Record<PromoCodeType, string> = {
  DISCOUNT: 'Discount',
  STAKING_BOOSTER: 'Staking Booster',
  CASHBACK_TOKEN: 'Cashback Token',
  PTO_BONUS: 'PTO Bonus',
}

/** Type filter options (the "All Types" sentinel is added at the call site). */
export const PROMO_CODE_TYPE_OPTIONS: { value: PromoCodeType; label: string }[] = (
  Object.entries(PROMO_CODE_TYPE_CONFIG) as [PromoCodeType, string][]
).map(([value, label]) => ({ value, label }))

export const PROMO_CODE_TYPE_FROM_API: Record<PromoCodeApiType, PromoCodeType> = {
  discount: 'DISCOUNT',
  staking_booster: 'STAKING_BOOSTER',
  cashback_token: 'CASHBACK_TOKEN',
  pto_bonus: 'PTO_BONUS',
}

export const PROMO_CODE_TYPE_TO_API: Record<PromoCodeType, PromoCodeApiType> = {
  DISCOUNT: 'discount',
  STAKING_BOOSTER: 'staking_booster',
  CASHBACK_TOKEN: 'cashback_token',
  PTO_BONUS: 'pto_bonus',
}

/**
 * Backend status codes for promo code: 1 = scheduled, 2 = active, 3 = expired.
 * The adapter also tolerates a raw status string ("scheduled"/"active"/"expired").
 */
export const PROMO_CODE_STATUS_FROM_CODE: Record<number, PromoCodeStatus> = {
  1: 'SCHEDULED',
  2: 'ACTIVE',
  3: 'EXPIRED',
}

export const PROMO_CODE_STATUS_TO_CODE: Record<PromoCodeStatus, number> = {
  SCHEDULED: 1,
  ACTIVE: 2,
  EXPIRED: 3,
}

export const PROMO_CODE_SEARCH_MAX_LENGTH = 255

/** Status badge styling for PTOs in the "Total PTOs Applied" popup. */
export const APPLIED_PTO_STATUS_CONFIG: Record<
  AppliedPtoStatus,
  { label: string; color: string; background: string }
> = {
  ACTIVE: { label: 'Active', color: '#008236', background: '#dcfce7' },
  INACTIVE: { label: 'Inactive', color: '#364153', background: '#f3f4f6' },
}

/** Status filter options (the "All Status" sentinel is added at the call site). */
export const APPLIED_PTO_STATUS_OPTIONS: { value: AppliedPtoStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
]

export const PROMO_CODE_FIELD_MAX_LENGTH = {
  code: 50,
  /** numeric digits */
  value: 2,
  /** numeric digits */
  totalSupply: 10,
}

export const PROMO_CODE_TOKEN_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Mey', label: 'Mey' },
]

export const PROMO_CODE_TOTAL_SUPPLY_TYPE_OPTIONS: {
  value: PromoCodeTotalSupplyType
  label: string
}[] = [
  { value: 'UNLIMITED', label: 'Unlimited' },
  { value: 'LIMITED', label: 'Limited' },
]

export const PROMO_CODE_USAGE_LIMIT_OPTIONS: { value: PromoCodeUsageLimit; label: string }[] = [
  { value: 'UNLIMITED', label: 'Unlimited' },
  { value: 'ONE_PER_USER', label: 'One code per user' },
]

export const PROMO_CODE_PTO_SOLD_OUT_TOOLTIP =
  'When the switch is OFF, users who buy PTO and apply promo code can claim cashback/bonus immediately. When the switch is ON, users who buy PTO and apply promo code can claim cashback/bonus once PTO is sold out.'

export const PROMO_CODE_MESSAGES = {
  /** PM_S01 */
  DELETE_SUCCESS: 'Delete promo code successfully',
  /** CREATE_PM_S01 */
  CREATE_SUCCESS: 'Create promo code successfully',
  /** EDIT_PM_S01 */
  EDIT_SUCCESS: 'Save changes successfully',
  /** CREATE_PM_E01 */
  CODE_EXISTS: 'Code content already exists',
  /** CREATE_PM_E02 */
  MIN_ONE: (field: string) => `${field} cannot be smaller than 1`,
  /** CREATE_PM_E03 */
  END_BEFORE_START: 'End date cannot be smaller than start date',
  REQUIRED: (field: string) => `${field} is required`,
}

export const PROMO_CODE_GENERATED_LENGTH = 8

const PROMO_CODE_GENERATE_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

/** Generate an 8-char random alphanumeric code (CREATE/EDIT "Generate Code"). */
export const generatePromoCode = (): string => {
  let out = ''
  for (let i = 0; i < PROMO_CODE_GENERATED_LENGTH; i++) {
    out += PROMO_CODE_GENERATE_CHARSET.charAt(randomInt(PROMO_CODE_GENERATE_CHARSET.length))
  }
  return out
}

/** Prefix/suffix shown inside the Value input, per type. */
export const getPromoCodeValueAffix = (
  type: PromoCodeType
): { prefix?: string; suffix?: string } => {
  switch (type) {
    case 'STAKING_BOOSTER':
      return { prefix: '+', suffix: '%' }
    case 'DISCOUNT':
    case 'CASHBACK_TOKEN':
      return { suffix: '%' }
    default:
      return {}
  }
}

export type PromoCodeEditableField =
  | 'code'
  | 'type'
  | 'value'
  | 'tokenType'
  | 'ptoSoldOut'
  | 'totalSupply'
  | 'usageLimit'
  | 'duration'
  | 'linkedPtos'

/**
 * Which fields are editable in edit mode, keyed by the code's status
 * (per "Admin - View Code Details / Edit Code").
 */
const PROMO_CODE_EDITABLE_BY_STATUS: Record<PromoCodeEditableField, PromoCodeStatus[]> = {
  code: ['SCHEDULED'],
  type: ['SCHEDULED'],
  value: ['SCHEDULED'],
  tokenType: ['SCHEDULED'],
  ptoSoldOut: ['SCHEDULED'],
  totalSupply: ['SCHEDULED'],
  usageLimit: ['SCHEDULED'],
  duration: ['SCHEDULED', 'ACTIVE', 'EXPIRED'],
  linkedPtos: ['SCHEDULED', 'ACTIVE'],
}

export const isPromoCodeFieldEditable = (
  field: PromoCodeEditableField,
  mode: PromoCodeFormMode,
  status?: PromoCodeStatus
): boolean => {
  if (mode === 'create') return true
  if (mode === 'view') return false
  if (!status) return false
  return PROMO_CODE_EDITABLE_BY_STATUS[field].includes(status)
}

/**
 * Value line shown under the code content, per type:
 * - Discount → "{n}% off"
 * - Staking Booster → "+{n}% Reward"
 * - Cashback Token → "{n}% {tokenType} cashback"
 * - PTO Bonus → "Buy {buy} get {get} PTO"
 */
export const formatPromoCodeValue = (item: PromoCodeItem): string => {
  switch (item.type) {
    case 'DISCOUNT':
      return item.value == null ? '' : `${item.value}% off`
    case 'STAKING_BOOSTER':
      return item.value == null ? '' : `+${item.value}% Reward`
    case 'CASHBACK_TOKEN':
      if (item.value == null) return ''
      return item.tokenType
        ? `${item.value}% ${item.tokenType} cashback`
        : `${item.value}% cashback`
    case 'PTO_BONUS':
      return item.bonusBuy == null && item.bonusGet == null
        ? ''
        : `Buy ${item.bonusBuy ?? 0} get ${item.bonusGet ?? 0} PTO`
  }
}

/**
 * "Used Times" cell: just the count when supply is unlimited, otherwise
 * "{used}/{totalSupply}".
 */
export const formatPromoCodeUsedTimes = (usedTimes: number, totalSupply?: number): string => {
  if (totalSupply == null) return `${usedTimes}`
  return `${usedTimes}/${totalSupply}`
}

/*********************Whitelist & Email (distributions)********************/

export const DISTRIBUTION_TYPE_TO_API: Record<PromoCodeDistributionType, number> = {
  ALL_USERS: 1,
  REFERRED_USERS: 2,
  EXISTING_USERS: 3,
  IMPORT_FILE: 4,
}

export const DISTRIBUTION_TYPE_FROM_API: Record<number, PromoCodeDistributionType> = {
  1: 'ALL_USERS',
  2: 'REFERRED_USERS',
  3: 'EXISTING_USERS',
  4: 'IMPORT_FILE',
}

export const DISTRIBUTION_TYPE_LABEL: Record<PromoCodeDistributionType, string> = {
  ALL_USERS: 'All users',
  REFERRED_USERS: 'Referred users',
  EXISTING_USERS: 'Existing users',
  IMPORT_FILE: 'Import file',
}

/** Type options for the Setup radio and the History filter ("All types" added at call site). */
export const DISTRIBUTION_TYPE_OPTIONS: { value: PromoCodeDistributionType; label: string }[] = (
  Object.entries(DISTRIBUTION_TYPE_LABEL) as [PromoCodeDistributionType, string][]
).map(([value, label]) => ({ value, label }))

export const DISTRIBUTION_SEND_MODE_TO_API: Record<PromoCodeDistributionSendMode, number> = {
  ALL_WHITELIST: 1,
  NOT_EMAILED_ONLY: 2,
}

export const DISTRIBUTION_SEND_MODE_OPTIONS: {
  value: PromoCodeDistributionSendMode
  label: string
}[] = [
  { value: 'ALL_WHITELIST', label: 'All whitelist users' },
  { value: 'NOT_EMAILED_ONLY', label: 'Only users not emailed before' },
]

/** PlatformPromoCodeDistributionStatusEnum on the BE. */
export const DISTRIBUTION_EMAIL_STATUS_TO_API: Record<PromoCodeDistributionEmailStatus, number> = {
  NOT_SENT: 1,
  SENDING: 2,
  SENT: 3,
  PARTIAL: 4,
  FAILED: 5,
}

export const DISTRIBUTION_EMAIL_STATUS_FROM_API: Record<number, PromoCodeDistributionEmailStatus> =
  {
    1: 'NOT_SENT',
    2: 'SENDING',
    3: 'SENT',
    4: 'PARTIAL',
    5: 'FAILED',
  }

/** Email-status filter options ("All statuses" added at call site). */
export const DISTRIBUTION_EMAIL_STATUS_OPTIONS: {
  value: PromoCodeDistributionEmailStatus
  label: string
}[] = [
  { value: 'NOT_SENT', label: 'Not sent' },
  { value: 'SENDING', label: 'Sending' },
  { value: 'SENT', label: 'Sent (all ok)' },
  { value: 'PARTIAL', label: 'Has failures' },
  { value: 'FAILED', label: 'Failed' },
]

export const DISTRIBUTION_SUBJECT_MAX_LENGTH = 300

export const DISTRIBUTION_IMPORT_ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv']
export const DISTRIBUTION_IMPORT_ACCEPT = '.xlsx,.xls,.csv'
export const DISTRIBUTION_IMPORT_MAX_SIZE_MB = 5
export const DISTRIBUTION_TEMPLATE_FILENAME = 'promo-code-whitelist-template.csv'
export const DISTRIBUTION_TEMPLATE_HEADER = 'email'

export const DISTRIBUTION_MESSAGES = {
  /** PM_S02 */
  SAVE_SUCCESS: 'Save whitelist and send email successfully',
  INVALID_FILE_TYPE: 'Only .xlsx, .xls and .csv files are allowed',
  FILE_TOO_LARGE: `File size cannot exceed ${DISTRIBUTION_IMPORT_MAX_SIZE_MB}MB`,
  NO_EMAILS_IN_FILE: 'No valid emails found in the uploaded file',
}

/**
 * "Whitelist & Email" cell label:
 * All users / Referred users / "{n} Users" (Existing) / "{n} Emails" (Import file);
 * undefined summary => not set up yet (cell shows "Set up"). Falls back to the
 * type label when the BE doesn't send the user count.
 */
export const formatDistributionSummary = (summary?: PromoCodeDistributionSummary): string => {
  if (!summary) return 'Set up'
  switch (summary.type) {
    case 'EXISTING_USERS':
      return summary.totalUsers != null
        ? `${summary.totalUsers} Users`
        : DISTRIBUTION_TYPE_LABEL[summary.type]
    case 'IMPORT_FILE':
      return summary.totalUsers != null
        ? `${summary.totalUsers} Emails`
        : DISTRIBUTION_TYPE_LABEL[summary.type]
    default:
      return DISTRIBUTION_TYPE_LABEL[summary.type]
  }
}
